import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Cookie, X } from "lucide-react";

const LS_KEY = "memento_cookie_consent_v1";

/**
 * Cookie banner — GDPR baseline.
 * Shows once. Persists user choice in localStorage.
 * We only use "strictly necessary" cookies (session), so we don't need
 * granular consent categories, but we display them anyway for transparency.
 */
export function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY);
    if (!saved) setTimeout(() => setVisible(true), 800);
  }, []);

  const dismiss = (choice) => {
    localStorage.setItem(LS_KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div data-testid="cookie-banner" className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-50 animate-in slide-in-from-bottom-5">
      <div className="sp-card p-5 shadow-xl border-2 border-[color:var(--sp-brand)]/20">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-xl bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)] flex items-center justify-center flex-shrink-0">
            <Cookie className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">{t("cookie.title")}</div>
            <p className="text-xs text-[color:var(--sp-text-secondary)] mt-1 leading-relaxed">
              <Trans i18nKey="cookie.body" components={{ 1: <Link to="/legal/privacy" className="text-[color:var(--sp-brand)] font-bold underline" /> }} />
            </p>
            <div className="mt-3 flex gap-2">
              <button data-testid="cookie-accept" onClick={() => dismiss("accepted")} className="sp-btn-primary text-xs !py-2 flex-1">{t("cookie.accept")}</button>
              <Link to="/legal/privacy" onClick={() => dismiss("read")} className="sp-btn-ghost text-xs !py-2 inline-flex items-center justify-center">{t("cookie.details")}</Link>
            </div>
          </div>
          <button data-testid="cookie-close" onClick={() => dismiss("dismissed")} className="text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-text)]">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
